import { getTradeOrder, getTradeOrderBuyer, getTradeOrders, getTradeStats } from '@/lib/api/tradeApi'
import storage from '@/lib/storage'
import { MerchantRegisterApplicationStatus, UserRole } from '@/typings/domain'
import {
  MerchantType,
  type PaidStatus,
  type TradeBuyer,
  type TradeDuration,
  type TradeOrder,
  type TradeOrderDetail,
  type TradePageInfo,
  type TradeStats,
} from '@/typings/trade'
import { createAsyncThunk, createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type { RootState } from '../store'
import type { CurrentUser } from './currentUserSlice'
import type { QueryState } from './queryStateType'

type TradeState = {
  merchantTypeOverride: MerchantType | null
  merchantTypeRestored: boolean
  duration: TradeDuration
  paidStatus: PaidStatus | 'all'
  orders: TradeOrder[]
  currentPage: number
  pageSize: number
  totalCount: number
  // requestId of the latest page-1 request; responses from older queries are dropped.
  ordersGeneration: string | null
  ordersState: QueryState
  stats: TradeStats | null
  latestStatsRequestId: string | null
  statsState: QueryState
  orderDetails: Record<string, { order: TradeOrderDetail; buyer: TradeBuyer | null }>
  orderDetailStates: Record<string, QueryState>
}

type OrdersRejectValue = { message: string; generation: string | null }

const initialQueryState: QueryState = { isLoading: false, loaded: false, error: null }

const initialState: TradeState = {
  merchantTypeOverride: null,
  merchantTypeRestored: false,
  duration: 'last30days',
  paidStatus: 'all',
  orders: [],
  currentPage: 0,
  pageSize: 20,
  totalCount: 0,
  ordersGeneration: null,
  ordersState: initialQueryState,
  stats: null,
  latestStatsRequestId: null,
  statsState: initialQueryState,
  orderDetails: {},
  orderDetailStates: {},
}

const merchantTypeStorageKey = (billingAccountId: string) => `trade_merchant_type:${billingAccountId}`

export const tradeSlice = createSlice({
  name: 'trade',
  initialState,
  reducers: {
    setMerchantTypeOverride: (state, action: PayloadAction<MerchantType | null>) => {
      // Orders and stats belong to one merchant; drop them so the other merchant's data never shows under it.
      if (state.merchantTypeRestored && state.merchantTypeOverride !== action.payload) {
        state.orders = []
        state.currentPage = 0
        state.totalCount = 0
        state.ordersState = initialQueryState
        state.stats = null
        state.statsState = initialQueryState
      }
      state.merchantTypeOverride = action.payload
      state.merchantTypeRestored = true
    },
    setTradeDuration: (state, action: PayloadAction<TradeDuration>) => {
      state.duration = action.payload
    },
    setTradePaidStatus: (state, action: PayloadAction<PaidStatus | 'all'>) => {
      state.paidStatus = action.payload
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchTradeOrdersThunk.pending, (state, action) => {
        if (action.meta.arg.page === 1) state.ordersGeneration = action.meta.requestId
        state.ordersState.isLoading = true
      })
      .addCase(fetchTradeOrdersThunk.fulfilled, (state, action) => {
        const { page } = action.meta.arg
        if (action.payload.generation !== state.ordersGeneration) return
        if (page > 1 && state.currentPage !== page - 1) return

        state.orders = page === 1 ? action.payload.data : [...state.orders, ...action.payload.data]
        state.currentPage = page
        state.pageSize = action.payload.pageInfo.pageSize
        state.totalCount = action.payload.pageInfo.totalCount
        state.ordersState = { isLoading: false, loaded: true, error: null }
      })
      .addCase(fetchTradeOrdersThunk.rejected, (state, action) => {
        const generation = action.payload?.generation ?? (action.meta.arg.page === 1 ? action.meta.requestId : null)
        if (generation !== state.ordersGeneration) return
        state.ordersState.isLoading = false
        state.ordersState.error = action.payload?.message ?? action.error.message
      })
      .addCase(fetchTradeStatsThunk.pending, (state, action) => {
        state.latestStatsRequestId = action.meta.requestId
        state.statsState.isLoading = true
      })
      .addCase(fetchTradeStatsThunk.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.latestStatsRequestId) return
        state.stats = action.payload
        state.statsState = { isLoading: false, loaded: true, error: null }
      })
      .addCase(fetchTradeStatsThunk.rejected, (state, action) => {
        if (action.meta.requestId !== state.latestStatsRequestId) return
        state.statsState.isLoading = false
        state.statsState.error = (action.payload as string) ?? action.error.message
      })
      .addCase(fetchTradeOrderThunk.pending, (state, action) => {
        const prev = state.orderDetailStates[action.meta.arg] ?? initialQueryState
        state.orderDetailStates[action.meta.arg] = { ...prev, isLoading: true, error: null }
      })
      .addCase(fetchTradeOrderThunk.fulfilled, (state, action) => {
        state.orderDetails[action.meta.arg] = action.payload
        state.orderDetailStates[action.meta.arg] = { isLoading: false, loaded: true, error: null }
      })
      .addCase(fetchTradeOrderThunk.rejected, (state, action) => {
        const prev = state.orderDetailStates[action.meta.arg] ?? initialQueryState
        state.orderDetailStates[action.meta.arg] = {
          ...prev,
          isLoading: false,
          error: (action.payload as string) ?? action.error.message,
        }
      })
  },
})

export const fetchTradeOrdersThunk = createAsyncThunk<
  { data: TradeOrder[]; pageInfo: TradePageInfo; generation: string | null },
  { page: number },
  { state: RootState; rejectValue: OrdersRejectValue }
>('trade/fetchOrders', async ({ page }, thunkAPI) => {
  const state = thunkAPI.getState()
  // Page 1 starts a new generation (set in `pending`); later pages belong to the one they were loaded under.
  const generation = page === 1 ? thunkAPI.requestId : state.trade.ordersGeneration
  const type = selectTradeMerchantType(state)
  if (!type) return thunkAPI.rejectWithValue({ message: '没有可用的商户号', generation })

  const { data, error } = await getTradeOrders({
    type,
    page,
    duration: state.trade.duration,
    paidStatus: state.trade.paidStatus,
  })
  return data ? { ...data, generation } : thunkAPI.rejectWithValue({ message: error, generation })
})

export const fetchTradeStatsThunk = createAsyncThunk<TradeStats, void, { state: RootState }>(
  'trade/fetchStats',
  async (_, thunkAPI) => {
    const state = thunkAPI.getState()
    const type = selectTradeMerchantType(state)
    if (!type) return thunkAPI.rejectWithValue('没有可用的商户号')

    const { data, error } = await getTradeStats({ type, duration: state.trade.duration })
    return data?.data ?? thunkAPI.rejectWithValue(error)
  }
)

export const fetchTradeOrderThunk = createAsyncThunk<
  { order: TradeOrderDetail; buyer: TradeBuyer | null },
  string,
  { state: RootState }
>('trade/fetchOrder', async (id, thunkAPI) => {
  const { data, error } = await getTradeOrder(id)
  if (!data) return thunkAPI.rejectWithValue(error)

  const order = data.data
  let buyer: TradeBuyer | null = null
  // Buyer info is shown for quickpay pages only, and only the per-payment-page endpoint exposes it; a
  // failure here must not hide the order itself.
  if (order.sourceEntry.formScene === 'quickpay' && order.sourceEntry.formToken) {
    const buyerResult = await getTradeOrderBuyer(order.sourceEntry.formToken, id)
    buyer = buyerResult.data?.data.buyer ?? null
  }
  return { order, buyer }
})

export const selectTradeOrdersHasNextPage = (state: RootState) =>
  state.trade.currentPage * state.trade.pageSize < state.trade.totalCount

export const restoreTradeMerchantType = createAsyncThunk<void, void, { state: RootState }>(
  'trade/restoreMerchantType',
  async (_, thunkAPI) => {
    const billingAccountId = thunkAPI.getState().currentUser.user?.billingAccount?.id
    if (!billingAccountId) return
    thunkAPI.dispatch(setMerchantTypeOverride(storage.get<MerchantType>(merchantTypeStorageKey(billingAccountId))))
  }
)

export const switchTradeMerchantType = createAsyncThunk<void, MerchantType, { state: RootState }>(
  'trade/switchMerchantType',
  async (type, thunkAPI) => {
    const billingAccountId = thunkAPI.getState().currentUser.user?.billingAccount?.id
    if (billingAccountId) storage.set(merchantTypeStorageKey(billingAccountId), type)
    thunkAPI.dispatch(setMerchantTypeOverride(type))
  }
)

type TradeBillingAccount = NonNullable<CurrentUser['billingAccount']>

export function getGdMerchantState(billingAccount: TradeBillingAccount | null | undefined) {
  const status = billingAccount?.gdMerchant?.merchantRegisterApplicationStatus
  if (!status) return 'none' as const
  if (billingAccount.merchantAccounts.length > 0 || status === MerchantRegisterApplicationStatus.ChannelIdentified) {
    return 'opened' as const
  }
  return 'applying' as const
}

export function hasDirectMerchant(billingAccount: TradeBillingAccount | null | undefined) {
  return !!billingAccount && billingAccount.wxpayAccounts.length + billingAccount.alipayAccounts.length > 0
}

export const selectAvailableMerchantTypes = createSelector(
  [(state: RootState) => state.currentUser.user?.billingAccount],
  billingAccount => {
    const types: MerchantType[] = []
    if (getGdMerchantState(billingAccount) === 'opened') types.push(MerchantType.GdMerchant)
    if (hasDirectMerchant(billingAccount)) types.push(MerchantType.Direct)
    return types
  }
)

// Matches the PC web: everyone but outworkers can view trades.
export const selectCanAccessTrade = (state: RootState) =>
  !!state.currentUser.user &&
  state.currentUser.user.role !== UserRole.Outworker &&
  selectAvailableMerchantTypes(state).length > 0

export const selectTradeMerchantType = (state: RootState): MerchantType | null => {
  const available = selectAvailableMerchantTypes(state)
  const override = state.trade.merchantTypeOverride
  if (override && available.includes(override)) return override
  return available[0] ?? null
}

export const { setMerchantTypeOverride, setTradeDuration, setTradePaidStatus } = tradeSlice.actions

export default tradeSlice.reducer
